import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import {
  FaEdit,
  FaPlus,
  FaTrash,
  FaWallet,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
} from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import colors from '../constants/colors';

const getAuthToken = () => localStorage.getItem('authToken');

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    account_type_id: '',
    opening_balance: 0,
  });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [selectedAccountTypes, setSelectedAccountTypes] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total_records: 0,
    total_pages: 0,
  });

  const filterDropdownRef = useRef(null);

  useEffect(() => {
    fetchAccountTypes();
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [pagination.page, searchTerm, selectedAccountTypes]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (selectedAccountTypes.length > 0) {
        params.append('account_type_id', selectedAccountTypes.join(','));
      }

      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-accounts?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        },
      );
      setAccounts(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch accounts', error);
      setError('Failed to load accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-account-types`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        },
      );

      setAccountTypes(response.data.data);
    } catch (error) {
      console.error('Failed to fetch account types', error);
    }
  };

  const handleShowModal = (account = null) => {
    if (account) {
      setFormData({
        name: account.name,
        account_type_id: account.account_type_id,
        description: account.description,
        opening_balance: account.opening_balance,
      });
      setEditingId(account.id);
    } else {
      setFormData({ name: '', account_type_id: '' });
      setEditingId(null);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Account name is required.');
      return;
    }
    if (!formData.account_type_id) {
      alert('Please select an account type.');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      let response;

      if (editingId) {
        response = await axios.post(
          `${appConstants.API_ROOT_URL}update-account`,
          {
            id: editingId,
            name: formData.name,
            account_type_id: formData.account_type_id,
            opening_balance: formData.opening_balance,
          },
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
            },
          },
        );
      } else {
        response = await axios.post(
          `${appConstants.API_ROOT_URL}create-account`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }

      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchAccounts();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save account', error);

      // Show server error message if available
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert('An error occurred while saving the account.');
      }
    } finally {
      setSaving(false);
    }
  };

  const getAccountTypeName = (typeId) => {
    const type = accountTypes.find((t) => t.id === typeId);
    return type ? type.name : 'N/A';
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleAccountTypeFilter = (typeId) => {
    setSelectedAccountTypes((prev) => {
      const newSelection = prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId];
      return newSelection;
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearAllFilters = () => {
    setSelectedAccountTypes([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                marginRight: 16,
              }}
            >
              <FaWallet />
              Accounts
            </span>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.sidebar.text,
                  fontSize: '0.9rem',
                }}
              />
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  paddingLeft: '35px',
                  height: '30px',
                  backgroundColor: colors.card.background,
                  borderColor: colors.card.border,
                  color: colors.text.primary,
                }}
              />
            </div>

            {/* Filter Dropdown */}
            <div style={{ position: 'relative' }} ref={filterDropdownRef}>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                style={{
                  width: '80px',
                  height: '30px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                }}
                title="Filter by Account Type"
              >
                <FaFilter className="me-1" />
                Filter
                {selectedAccountTypes.length > 0 && (
                  <Badge
                    bg="danger"
                    style={{
                      marginLeft: '4px',
                      fontSize: '0.7rem',
                      padding: '2px 5px',
                      borderRadius: '8px',
                    }}
                  >
                    {selectedAccountTypes.length}
                  </Badge>
                )}
              </Button>

              {showFilterDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '250px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e9ecef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: '600',
                        color: '#495057',
                        fontSize: '0.9rem',
                      }}
                    >
                      Account Types
                    </span>
                    {selectedAccountTypes.length > 0 && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={clearAllFilters}
                        style={{
                          fontSize: '0.8rem',
                          padding: 0,
                          textDecoration: 'none',
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                  {accountTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => handleAccountTypeFilter(type.id)}
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'background 0.2s ease',
                        background: selectedAccountTypes.includes(type.id)
                          ? '#f0f4ff'
                          : 'white',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          selectedAccountTypes.includes(type.id)
                            ? '#f0f4ff'
                            : 'white';
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedAccountTypes.includes(type.id)}
                        onChange={() => {}}
                        style={{ pointerEvents: 'none' }}
                      />
                      <span style={{ fontSize: '0.9rem', color: '#2c3e50' }}>
                        {type.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add Account Button */}
          <Button
            onClick={() => handleShowModal()}
            variant="light"
            size="sm"
            disabled={saving}
            style={{
              width: '32px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Add Account"
          >
            <FaPlus />
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Spinner
              animation="border"
              style={{ color: '#667eea', width: '50px', height: '50px' }}
            />
            <p style={{ marginTop: '20px', color: '#6c757d' }}>
              Loading accounts...
            </p>
          </div>
        ) : accounts.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <FaWallet
              style={{
                fontSize: '4rem',
                color: '#dee2e6',
                marginBottom: '20px',
              }}
            />
            <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
              No Accounts Found
            </h4>
            <p style={{ color: '#adb5bd' }}>
              {searchTerm || selectedAccountTypes.length > 0
                ? 'No accounts match your search criteria.'
                : 'Get started by creating your first account.'}
            </p>
            {!searchTerm && selectedAccountTypes.length === 0 && (
              <Button
                onClick={() => handleShowModal()}
                style={{
                  marginTop: '20px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                <FaPlus className="me-2" /> Create Account
              </Button>
            )}
          </div>
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'left',
                        width: '60px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'left',
                      }}
                    >
                      Account Name
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'left',
                      }}
                    >
                      Account Type
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'center',
                        width: '100px',
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'right',
                      }}
                    >
                      Opening Balance
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        borderBottom: '2px solid #dee2e6',
                        textAlign: 'center',
                        width: '100px',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account, index) => (
                    <tr
                      key={account.id}
                      style={{
                        background: index % 2 === 0 ? '#fff' : '#fafbfc',
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          index % 2 === 0 ? '#fff' : '#fafbfc';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#2c3e50',
                          fontWeight: '500',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {account.name}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Badge
                          bg="light"
                          text="dark"
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          {getAccountTypeName(account.account_type_id)}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {account.is_system === 1 ? (
                          <Badge
                            bg="secondary"
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              padding: '5px 10px',
                              borderRadius: '6px',
                            }}
                          >
                            System
                          </Badge>
                        ) : (
                          <Badge
                            bg="success"
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              padding: '5px 10px',
                              borderRadius: '6px',
                            }}
                          >
                            Custom
                          </Badge>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'right',
                          color: '#2c3e50',
                          fontWeight: '600',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        ₹
                        {parseFloat(
                          account.opening_balance || 0,
                        ).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {account.is_system === 0 ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(account)}
                            disabled={saving}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '0.85rem',
                            }}
                            title="Edit"
                          >
                            <FaEdit />
                          </Button>
                        ) : (
                          <Badge
                            bg="light"
                            text="muted"
                            style={{
                              fontSize: '0.8rem',
                              padding: '6px 10px',
                              borderRadius: '6px',
                            }}
                            title="System accounts cannot be edited"
                          >
                            Locked
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary and Pagination Footer */}
            <div
              style={{
                padding: '16px 20px',
                background: '#f8f9fa',
                borderTop: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
              }}
            >
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Showing {accounts.length} of {pagination.total_records} accounts
                {(searchTerm || selectedAccountTypes.length > 0) &&
                  ' (filtered)'}
              </span>

              {/* Pagination Controls */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <FaChevronLeft /> Prev
                </Button>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                    Page
                  </span>
                  <Badge
                    bg="primary"
                    style={{
                      fontSize: '0.9rem',
                      padding: '6px 12px',
                      borderRadius: '6px',
                    }}
                  >
                    {pagination.page} / {pagination.total_pages}
                  </Badge>
                </div>

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  style={{
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  Next <FaChevronRight />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderBottom: 'none',
          }}
        >
          <Modal.Title style={{ fontWeight: '700' }}>
            {editingId ? 'Edit Account' : 'Add New Account'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                Account Name <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter account name"
                style={{
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  padding: '10px 15px',
                  transition: 'all 0.3s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow =
                    '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                Account Type <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Select
                value={formData.account_type_id}
                onChange={(e) =>
                  setFormData({ ...formData, account_type_id: e.target.value })
                }
                style={{
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  padding: '10px 15px',
                  transition: 'all 0.3s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow =
                    '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="">Select Account Type</option>
                {accountTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                Opening Balance
              </Form.Label>
              <Form.Control
                type="number"
                value={formData?.opening_balance}
                onChange={(e) =>
                  setFormData({ ...formData, opening_balance: e.target.value })
                }
                placeholder="Enter opening balance"
                step="0.01"
                style={{
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  padding: '10px 15px',
                  transition: 'all 0.3s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow =
                    '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{ borderTop: '1px solid #dee2e6', padding: '20px 30px' }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
            }}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              <>{editingId ? 'Update Account' : 'Create Account'}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default Accounts;
